import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import {
  ArrowUpRight,
  Search,
  PenLine,
  ClipboardList,
  Wrench,
  PackageCheck,
  LifeBuoy,
  Check,
} from "lucide-react";

const solutions = [
  {
    id: "office-fitout",
    title: "Turnkey Office Fit-out",
    body: "Modular workstations, meeting rooms, cabins and breakout spaces delivered site-ready.",
    outcome: "Ready for occupancy in a single coordinated deployment.",
    tags: ["Corporate Offices", "Government", "Education", "Healthcare"],
    included: [
      "Space planning & design",
      "Furniture procurement",
      "Electrical & lighting",
      "HVAC coordination",
      "Snagging & final handover",
    ],
    metric: "End-to-End Delivery",
    metadata: ["End-to-end Delivery", "Turnkey Deployment", "Installation Included", "Multi-location Support"],
    img: "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  },
  {
    id: "institutional-supply",
    title: "Institutional Supply",
    body: "Furniture, equipment, consumables and technical goods for education, healthcare and PSUs.",
    outcome: "Simplified procurement with assured quality and timely delivery.",
    tags: ["Universities", "Hospitals", "Hotels", "Industries"],
    included: [
      "Multi-category sourcing",
      "Sample approvals",
      "Batch QA/QC",
      "Warehousing & despatch",
      "Post-delivery documentation",
    ],
    metric: "PAN India Network",
    metadata: ["Pan India Execution", "Vendor Network", "Compliance Ready", "Procurement Managed"],
    img: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  },
  {
    id: "logistics",
    title: "Logistics & Distribution",
    body: "Warehousing, last-mile coordination and multi-location supply orchestration.",
    outcome: "Predictable delivery windows across every drop-off point.",
    tags: ["Retail Chains", "Manufacturing", "Public Sector", "E-commerce"],
    included: [
      "Route & load planning",
      "Warehouse handling",
      "Fragile & special cargo",
      "GPS-tracked despatch",
      "POD & MIS reporting",
    ],
    metric: "Multi-location Execution",
    metadata: ["Multi-location Support", "Vendor Network", "Pan India Execution", "End-to-end Delivery"],
    img: "https://images.unsplash.com/photo-1519003722824-194d4455a60c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  },
  {
    id: "custom",
    title: "Customised Supply Solutions",
    body: "Bespoke sourcing and procurement programmes tailored to sector-specific compliance.",
    outcome: "Compliance-ready supply, engineered around your operating reality.",
    tags: ["Defence PSUs", "Healthcare", "Energy", "Infrastructure"],
    included: [
      "Requirement scoping",
      "OEM & vendor mapping",
      "Compliance & certification",
      "Contract structuring",
      "Long-term supply management",
    ],
    metric: "Compliance Ready",
    metadata: ["Compliance Ready", "Procurement Managed", "Vendor Network", "Turnkey Deployment"],
    img: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  },
];

const processSteps = [
  { icon: Search, label: "Discover" },
  { icon: PenLine, label: "Plan" },
  { icon: ClipboardList, label: "Source" },
  { icon: Wrench, label: "Execute" },
  { icon: PackageCheck, label: "Deliver" },
  { icon: LifeBuoy, label: "Support" },
];

const signatureNodes = [
  { label: "Requirement", desc: "Understanding operational objectives" },
  { label: "Strategy", desc: "Technical planning & budgeting" },
  { label: "Procurement", desc: "Verified sourcing network" },
  { label: "Execution", desc: "Coordinated implementation" },
  { label: "Quality Control", desc: "Inspection & compliance" },
  { label: "Delivery", desc: "Ready for immediate operations" },
];

const trustStatements = ["Procurement", "Project Execution", "Logistics", "Technical Support"];

export default function Solutions() {
  return (
    <section
      id="solutions"
      data-testid="solutions-section"
      className="relative bg-[var(--nv-paper)] text-[var(--nv-navy)] py-28 md:py-40 overflow-hidden"
    >
      {/* Architectural blueprint layer — continues the Hero's geometric language */}
      <SolutionsBlueprintLayer />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-12 gap-6 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-6">
            <div className="overline text-[var(--nv-gold)] flex items-center gap-3">
              <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
              <span>Solutions — Programmes</span>
            </div>
            <h2 className="mt-6 display text-5xl md:text-7xl" data-testid="solutions-heading">
              Ready-to-deploy <span className="italic text-[var(--nv-gold)]">programmes.</span>
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 md:col-start-9 text-[15px] leading-relaxed text-[var(--nv-navy)]/70">
            A curated portfolio of solutions we deliver end-to-end. Every programme is scoped,
            priced and reported transparently.
          </p>
        </div>

        {/* Process ribbon */}
        <ProcessRibbon />

        {/* First two cards */}
        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-6 md:gap-8" data-testid="solutions-grid">
          {solutions.slice(0, 2).map((s, i) => (
            <SolutionCard key={s.id} s={s} i={i} />
          ))}
        </div>
      </div>

      {/* Signature visual moment — full-width navy band */}
      <SignatureBand />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        {/* Remaining cards */}
        <div className="mt-20 md:mt-32 grid grid-cols-12 gap-6 md:gap-8">
          {solutions.slice(2).map((s, i) => (
            <SolutionCard key={s.id} s={s} i={i + 2} />
          ))}
        </div>

        {/* Closing conversion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.7, 0, 0.3, 1] }}
          className="mt-24 md:mt-32 border-t border-[var(--nv-line)] pt-16 md:pt-20"
          data-testid="solutions-closing-cta"
        >
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="overline text-[var(--nv-gold)] flex items-center gap-3">
                <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
                <span>Tailored to Your Organisation</span>
              </div>
              <h3 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Need a solution tailored to your <span className="italic text-[var(--nv-gold)]">organisation?</span>
              </h3>
              <p className="mt-6 text-[15px] leading-relaxed text-[var(--nv-navy)]/70 max-w-2xl">
                We combine procurement expertise, project execution and operational support to
                deliver reliable outcomes across sectors.
              </p>

              {/* Trust statements */}
              <div
                className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3"
                data-testid="closing-trust-statements"
              >
                {trustStatements.map((t, i) => (
                  <div key={t} className="flex items-center gap-4">
                    <span className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-[var(--nv-gold)]" strokeWidth={2} />
                      <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--nv-navy)]/75">
                        {t}
                      </span>
                    </span>
                    {i < trustStatements.length - 1 && (
                      <span className="w-4 h-px bg-[var(--nv-navy)]/20" aria-hidden />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-12 md:col-span-4 md:justify-self-end">
              <a href="#contact" className="btn-gold" data-testid="solutions-cta-primary">
                Discuss Your Requirement <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* -------------------------------------------------- */
/* Process ribbon                                     */
/* -------------------------------------------------- */
function ProcessRibbon() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.9, ease: [0.7, 0, 0.3, 1] }}
      className="relative pt-8 border-t border-[var(--nv-line)]"
      data-testid="solutions-process-ribbon"
    >
      <div className="flex items-center gap-3 mb-8">
        <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
        <span className="overline text-[var(--nv-navy)]/60">Our Method</span>
      </div>

      <div className="relative">
        {/* thin gold rail */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 1.6, ease: [0.7, 0, 0.3, 1], delay: 0.2 }}
          className="absolute left-0 right-0 top-1/2 h-px bg-[var(--nv-gold)]/70 origin-left"
        />
        <div className="relative grid grid-cols-3 md:grid-cols-6 gap-y-8">
          {processSteps.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 0.6, delay: 0.35 + i * 0.08 }}
                className="flex flex-col items-center text-center"
                data-testid={`process-step-${p.label.toLowerCase()}`}
              >
                <span className="relative z-10 inline-flex items-center justify-center w-11 h-11 rounded-full border border-[var(--nv-navy)]/25 bg-[var(--nv-paper)]">
                  <Icon className="w-4 h-4 text-[var(--nv-navy)]" strokeWidth={1.4} />
                </span>
                <div className="mt-3 font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--nv-navy)]/70">
                  {p.label}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

/* -------------------------------------------------- */
/* Solution card                                      */
/* -------------------------------------------------- */
function SolutionCard({ s, i }) {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [-30, 40]);

  return (
    <motion.article
      ref={cardRef}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: [0.7, 0, 0.3, 1], delay: (i % 2) * 0.1 }}
      className={`col-span-12 md:col-span-6 group ${i % 2 === 1 ? "md:mt-16" : ""}`}
      data-testid={`solution-card-${s.id}`}
    >
      <div className="relative overflow-hidden aspect-[4/5] md:aspect-[5/6] bg-[var(--nv-navy)]">
        {/* Parallax image */}
        <motion.div style={{ y: imgY }} className="absolute inset-[-6%]">
          <img
            src={s.img}
            alt={s.title}
            className="w-full h-full object-cover grayscale-[15%] contrast-[1.02] saturate-[0.9] transition-transform duration-[1600ms] ease-[cubic-bezier(0.7,0,0.3,1)] group-hover:scale-[1.04]"
            loading="lazy"
          />
        </motion.div>

        {/* Consistent dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--nv-navy)] via-[var(--nv-navy)]/55 to-[var(--nv-navy)]/25 opacity-[0.92] transition-opacity duration-700 group-hover:opacity-[0.82]" />

        {/* Metric — bottom-right */}
        <div
          className="absolute top-6 right-6 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--nv-navy)]/40 border border-[var(--nv-gold)]/40 backdrop-blur-sm"
          data-testid={`solution-metric-${s.id}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--nv-gold)]" />
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--nv-gold-soft)]">
            {s.metric}
          </span>
        </div>

        {/* Content overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-[var(--nv-paper)]">
          <div className="overline text-[var(--nv-gold)] mb-3">0{i + 1} — Solution</div>

          {/* Title with lift */}
          <h3 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.05] transition-transform duration-500 group-hover:-translate-y-1.5">
            {s.title}
          </h3>

          {/* Gold line under title */}
          <span
            className="mt-4 block h-px bg-[var(--nv-gold)] transition-[width] duration-[900ms] ease-[cubic-bezier(0.7,0,0.3,1)]"
            style={{ width: "36px" }}
            aria-hidden
          />
          <span className="sr-only">Accent line</span>

          {/* Applicability tags */}
          <div className="mt-4 flex flex-wrap gap-2" data-testid={`solution-tags-${s.id}`}>
            {s.tags.map((t) => (
              <span
                key={t}
                className="px-2.5 py-1 rounded-full border border-[var(--nv-gold)]/45 text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--nv-paper)]/85"
              >
                {t}
              </span>
            ))}
          </div>

          <p className="mt-5 text-sm md:text-base leading-relaxed text-[var(--nv-paper)]/85 max-w-md">
            {s.body}
          </p>
          <p className="mt-2 font-serif italic text-sm md:text-base text-[var(--nv-gold-soft)] max-w-md">
            Outcome — {s.outcome}
          </p>

          {/* Operational metadata strip — capsule labels */}
          <div
            className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 pt-4 border-t border-[var(--nv-paper)]/12"
            data-testid={`solution-metadata-${s.id}`}
          >
            {s.metadata.map((m) => (
              <span
                key={m}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--nv-paper)]/[0.08] text-[9.5px] font-mono uppercase tracking-[0.16em] text-[var(--nv-paper)]/75"
              >
                <span className="w-1 h-1 rounded-full bg-[var(--nv-gold)]" />
                {m}
              </span>
            ))}
          </div>

          {/* What's Included — revealed on hover / focus-within */}
          <div className="mt-5 max-w-md overflow-hidden">
            <div
              className="grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.7,0,0.3,1)] grid-rows-[0fr] opacity-0 group-hover:grid-rows-[1fr] group-hover:opacity-100 focus-within:grid-rows-[1fr] focus-within:opacity-100"
              data-testid={`solution-included-${s.id}`}
            >
              <div className="min-h-0">
                <div className="overline text-[var(--nv-gold)] mb-2 pt-4 border-t border-[var(--nv-paper)]/15">
                  What&apos;s Included
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                  {s.included.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[13px] text-[var(--nv-paper)]/85">
                      <Check className="w-3.5 h-3.5 text-[var(--nv-gold)] mt-0.5 shrink-0" strokeWidth={2} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* CTA — View Solution with animated underline + sliding arrow */}
          <a
            href="#contact"
            data-testid={`solution-enquire-${s.id}`}
            className="group/cta mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-[var(--nv-paper)] hover:text-[var(--nv-gold)] transition-colors duration-300"
          >
            <span className="relative pb-1">
              View Solution
              <span
                className="absolute left-0 bottom-0 h-px w-full bg-[var(--nv-gold)] origin-left scale-x-100 transition-transform duration-500"
                aria-hidden
              />
            </span>
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover/cta:translate-x-1 group-hover/cta:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </motion.article>
  );
}

/* -------------------------------------------------- */
/* Signature band — animated line + nodes             */
/* -------------------------------------------------- */
function SignatureBand() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.85", "end 0.35"],
  });
  // Gold line fills L→R as user scrolls through the band.
  const railScale = useTransform(scrollYProgress, [0, 1], [0, 1]);
  // Ambient reflected-light gradient position (0→100% along the band).
  const glowX = useTransform(scrollYProgress, [0, 1], ["-10%", "110%"]);

  return (
    <section
      ref={ref}
      className="relative my-24 md:my-32 bg-[var(--nv-navy)] text-[var(--nv-paper)] py-24 md:py-32 nv-grain overflow-hidden"
      data-testid="solutions-signature-band"
    >
      {/* Ambient navy→gold reflected light travelling with scroll */}
      <motion.div
        aria-hidden
        style={{ left: glowX }}
        className="pointer-events-none absolute top-1/2 -translate-y-1/2 h-[280px] w-[520px] -translate-x-1/2 opacity-[0.35]"
      >
        <div className="w-full h-full rounded-full blur-3xl bg-[radial-gradient(closest-side,rgba(197,160,89,0.35),rgba(197,160,89,0.05)_60%,transparent_80%)]" />
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.7, 0, 0.3, 1] }}
            className="overline text-[var(--nv-gold)] flex items-center justify-center gap-3"
          >
            <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
            <span>Signature Method</span>
            <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
          </motion.div>
          <motion.h3
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, ease: [0.7, 0, 0.3, 1], delay: 0.1 }}
            className="mt-8 display text-4xl md:text-6xl lg:text-7xl leading-[1.02]"
          >
            Every solution is engineered to move from{" "}
            <span className="italic text-[var(--nv-gold)]">concept to completion.</span>
          </motion.h3>
        </div>

        {/* Six-milestone line — scroll-driven fill + progressive illumination */}
        <div className="mt-20 md:mt-28 relative">
          <div className="relative">
            <div className="absolute left-0 right-0 top-[22px] h-px bg-[var(--nv-paper)]/15" aria-hidden />
            <motion.div
              style={{ scaleX: railScale }}
              className="absolute left-0 right-0 top-[22px] h-px bg-[var(--nv-gold)] origin-left"
              aria-hidden
            />

            <div className="relative grid grid-cols-3 md:grid-cols-6 gap-y-12">
              {signatureNodes.map((n, i) => (
                <SignatureMilestone
                  key={n.label}
                  n={n}
                  index={i}
                  total={signatureNodes.length}
                  progress={scrollYProgress}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SignatureMilestone({ n, index, total, progress }) {
  // Milestone "activates" when the gold rail has passed its position.
  // "Current" milestone is the one closest to the leading edge of the rail.
  const threshold = index / (total - 1);
  const activeRange = 1 / (total - 1);

  const activation = useTransform(progress, (v) => {
    const distance = v - threshold;
    if (v >= threshold) return 1; // completed
    if (distance > -activeRange * 0.6) return 0.85; // currently entering
    return 0.35; // upcoming
  });

  const pulseScale = useTransform(progress, (v) => {
    const distance = Math.abs(v - threshold);
    return distance < activeRange * 0.5 ? 1.08 : 1;
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: 0.55, ease: [0.7, 0, 0.3, 1], delay: 0.15 + index * 0.08 }}
      className="flex flex-col items-center text-center"
      data-testid={`signature-node-${index}`}
    >
      <motion.span
        style={{ opacity: activation, scale: pulseScale }}
        className="relative z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-[var(--nv-navy)] border border-[var(--nv-gold)]"
      >
        <motion.span
          style={{ opacity: activation }}
          className="w-2 h-2 rounded-full bg-[var(--nv-gold)]"
        />
      </motion.span>
      <motion.div
        style={{ opacity: activation }}
        className="mt-4 font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--nv-paper)]"
      >
        {n.label}
      </motion.div>
      <div className="mt-1 font-mono text-[10px] tracking-[0.22em] text-[var(--nv-gold)]/70">
        0{index + 1}
      </div>
      <motion.p
        style={{ opacity: activation }}
        className="mt-3 max-w-[16ch] mx-auto text-[11px] leading-relaxed text-[var(--nv-paper)]/55"
      >
        {n.desc}
      </motion.p>
    </motion.div>
  );
}


/* -------------------------------------------------- */
/* Architectural blueprint layer                       */
/* Continues the Hero's geometric vocabulary — same    */
/* stroke weight, same low opacity, same annotations.  */
/* Positioned behind content, aligned with the gold    */
/* trajectory so both systems read as one language.    */
/* -------------------------------------------------- */
function SolutionsBlueprintLayer() {
  return (
    <div className="absolute inset-0 pointer-events-none z-0" aria-hidden>
      <svg
        viewBox="0 0 1600 3200"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full opacity-[0.11]"
      >
        <defs>
          <pattern id="sol-grid-fine" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
            <path d="M 12 0 L 0 0 0 12" fill="none" stroke="#0A192F" strokeWidth="0.35" />
          </pattern>
          <pattern id="sol-grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#0A192F" strokeWidth="0.55" />
          </pattern>
        </defs>

        {/* Continuous ground grid */}
        <rect width="100%" height="100%" fill="url(#sol-grid-fine)" />
        <rect width="100%" height="100%" fill="url(#sol-grid)" />

        {/* Compass ring, top-right — mirrors the Hero's compass on continuity */}
        <g stroke="#0A192F" strokeWidth="0.7" fill="none">
          <circle cx="1440" cy="380" r="260" strokeDasharray="2 6" />
          <circle cx="1440" cy="380" r="190" />
          <circle cx="1440" cy="380" r="120" strokeDasharray="1 5" />
          <line x1="1440" y1="90" x2="1440" y2="670" strokeDasharray="1 5" />
          <line x1="1160" y1="380" x2="1720" y2="380" strokeDasharray="1 5" />
          <circle cx="1440" cy="380" r="4" fill="#C5A059" stroke="none" />
        </g>

        {/* Isometric volume, left-middle — same axonometric drafting as Hero */}
        <g stroke="#0A192F" strokeWidth="0.7" fill="none">
          <path d="M 180 1080 L 340 1000 L 500 1080 L 500 1240 L 340 1320 L 180 1240 Z" />
          <path d="M 340 1000 L 340 1160 L 500 1240" />
          <path d="M 340 1160 L 180 1080" />
          <path
            d="M 180 1080 L 340 1000 L 500 1080 L 340 1160 Z"
            fill="#C5A059"
            fillOpacity="0.08"
            stroke="#C5A059"
            strokeOpacity="0.45"
          />
          {/* dimension marker */}
          <line x1="520" y1="1080" x2="540" y2="1080" />
          <line x1="520" y1="1240" x2="540" y2="1240" />
          <line x1="535" y1="1080" x2="535" y2="1240" />
        </g>

        {/* Compass ring, middle-left — anchors the lower half of the section */}
        <g stroke="#0A192F" strokeWidth="0.6" fill="none">
          <circle cx="240" cy="2360" r="170" />
          <circle cx="240" cy="2360" r="90" strokeDasharray="2 6" />
          <line x1="240" y1="2170" x2="240" y2="2550" strokeDasharray="1 5" />
          <line x1="50" y1="2360" x2="430" y2="2360" strokeDasharray="1 5" />
          <circle cx="240" cy="2360" r="3" fill="#C5A059" stroke="none" />
        </g>

        {/* Blueprint plan block, bottom-right */}
        <g stroke="#0A192F" strokeWidth="0.7" fill="none">
          <path d="M 1180 2680 L 1360 2600 L 1540 2680 L 1540 2860 L 1360 2940 L 1180 2860 Z" />
          <path d="M 1360 2600 L 1360 2780 L 1540 2860" />
          <path d="M 1360 2780 L 1180 2680" />
        </g>

        {/* Draft construction lines — vertical + horizontal reveal marks
            aligned so they meet the gold trajectory's turning points. */}
        <g stroke="#0A192F" strokeOpacity="0.55" strokeWidth="0.6" fill="none">
          <line x1="820" y1="120" x2="820" y2="900" strokeDasharray="2 5" />
          <line x1="80" y1="1560" x2="1580" y2="1560" strokeDasharray="2 5" />
          <line x1="820" y1="2100" x2="820" y2="3080" strokeDasharray="2 5" />
        </g>

        {/* Small gold accent points — read as trajectory waypoints */}
        <g fill="#C5A059">
          <circle cx="1400" cy="220" r="2.6" />
          <circle cx="820" cy="880" r="2.6" />
          <circle cx="720" cy="1620" r="2.6" />
          <circle cx="820" cy="2440" r="2.6" />
          <circle cx="1080" cy="2960" r="2.6" />
        </g>

        {/* Corner dimension marks (same as Hero) */}
        <g stroke="#0A192F" strokeWidth="0.7" fill="none">
          <path d="M 30 30 L 62 30 M 30 30 L 30 62" />
          <path d="M 1570 30 L 1538 30 M 1570 30 L 1570 62" />
          <path d="M 30 3170 L 62 3170 M 30 3170 L 30 3138" />
          <path d="M 1570 3170 L 1538 3170 M 1570 3170 L 1570 3138" />
        </g>

        {/* Mono annotations — same drafting register as Hero */}
        <g
          fontFamily="'JetBrains Mono', ui-monospace, monospace"
          fill="#0A192F"
          fillOpacity="0.55"
          fontSize="10"
          letterSpacing="0.22em"
        >
          <text x="30" y="80">NV — 04 · SOLUTIONS</text>
          <text x="1570" y="80" textAnchor="end">SCALE 1:1</text>
          <text x="30" y="1600">CH · PROGRAMMES</text>
          <text x="1570" y="1600" textAnchor="end">FIG · D</text>
          <text x="30" y="3130">REV 01</text>
          <text x="1570" y="3130" textAnchor="end">SHEET 04/07</text>
        </g>
      </svg>
    </div>
  );
}
